import TxProcessingModal from '@/src/components/AppComponents/TxProcessingModal/TxProcessingModal';
import TxUserConfirmationModal from '@/src/components/AppComponents/TxUserConfirmationModal/TxUserConfirmationModal';
import { Button } from '@/src/components/Commons/Button';
import ToggleProController from '@/src/components/Commons/Controllers/ToggleProController/ToggleProController';
import TogleViewListOrDetailsController from '@/src/components/Commons/Controllers/TogleViewListOrDetailsController/TogleViewListOrDetailsController';
import LoaderButton from '@/src/components/Commons/LoaderButton/LoaderButton';
import { IFundDetailsForUsers } from '@/src/hooks/useFundDetailsForHome';
import { IFundListForUsers } from '@/src/hooks/useFundListForHome';
import { useList } from '@/src/hooks/useList';
import { useTokensStoreForFunds } from '@/src/hooks/useTokensStoreForFunds';
import { useTransactions } from '@/src/hooks/useTransactions';
import { hexToStr, SwapOfferSwapADAxFTTxParams, SwapOfferSwapFTxADATxParams } from '@/src/lib/MayzSmartDB';
import { SmartUTxOEntity, SwapOfferEntity } from '@/src/lib/MayzSmartDB/Entities/index';
import { SwapOfferApi, SwapOfferBtnHandlers, SwapOfferTxApiCalls } from '@/src/lib/MayzSmartDB/FrontEnd/index';
import { createToken_ADA_With_Amount, createToken_With_Amount, LookUpFields, LucidToolsFrontEnd, Token_With_Price_And_Date_And_Signature } from '@/src/lib/SmartDB';
import { showADA_Amount_NotRounded, showADA_Amount_Rounded, showToken_Amount_Rounded } from '@/src/store/global/tokensStoreModelHelpers';
import { useRouter } from '@/src/store/router/RouterProvider';
import { STATUS_ALLOW_SELL, STATUS_OPEN, SWAPS_REFRESH_TIME_MS } from '@/src/utils/constants';
import { ROUTES } from '@/src/utils/routes';
import { useEffect, useRef, useState } from 'react';
import Conversion from './Form/ControlsPro/Conversion';
import SwapForm from './Form/SwapForm';
import styles from './Swap.module.scss';

export default function Swap(props: IFundDetailsForUsers & IFundListForUsers) {
    //--------------------------------------
    let { protocol, fund, id } = props;
    //--------------------------------------
    const [isRefreshing, setIsRefreshing] = useState(true);
    useEffect(() => {
        setIsRefreshing(false);
    }, []);
    //--------------------------------------
    const router = useRouter();
    //--------------------------------------
    const [isBuy, setIsBuy] = useState<boolean>(true);
    //--------------------------------------
    const [isValidInputToGive, setIsValidInputToGive] = useState<boolean>(true);
    const [isValidInputToGet, setIsValidInputToGet] = useState<boolean>(true);
    //--------------------------------------
    const [amount_ADA, setAmount_ADA] = useState(0n);
    const [amount_FT, setAmount_FT] = useState(0n);
    const [commission_FT, setCommission_FT] = useState(0n);
    const [amount_AfterCommission_FT, setAmount_AfterCommission_FT] = useState(0n);
    const [commission_ADA, setCommission_ADA] = useState(0n);
    const [amount_AfterCommission_ADA, setAmount_AfterCommission_ADA] = useState(0n);
    //--------------------------------------
    const [maxAmount_FT_Available, setMaxAmount_FT_Available] = useState<bigint | undefined>(undefined);
    const [maxAmount_ADA_Available, setMaxAmount_ADA_Available] = useState<bigint | undefined>(undefined);
    //--------------------------------------
    const lastChanged = useRef('GET');
    //--------------------------------------
    const [price_Adjusted_For_Fee, setPrice_Adjusted_For_Fee] = useState<bigint | undefined>(undefined);
    //--------------------------------------
    const [isPriceValid, setIsPriceValid] = useState<boolean>(false);
    //--------------------------------------
    const loadSwapOfferList = async () => {
        //--------
        if (fund === undefined) {
            return [];
        }
        //--------
        console.log('[SWAPS] loadSwapOfferList');
        //--------
        const lookUpFields: LookUpFields[] = [
            {
                from: SmartUTxOEntity.className(),
                localField: 'smartUTxO_id',
                foreignField: '_id',
                as: 'smartUTxO',
                fieldsForSelect: { isPreparingForReading: true, isReading: true, isPreparingForConsuming: true, isConsuming: true },
            },
        ];
        //--------
        let params: Record<string, any> = {
            sodFundPolicy_CS: fund.getNET_id_CS(),
            sodSwapStatus: STATUS_OPEN,
            'smartUTxO.isPreparingForReading': { $exists: false },
            'smartUTxO.isReading': { $exists: false },
            'smartUTxO.isPreparingForConsuming': { $exists: false },
            'smartUTxO.isConsuming': { $exists: false },
        };
        let sort = {};
        if (isBuy) {
            params = { ...params, sodAllowSellFT: STATUS_ALLOW_SELL, sodAmount_FT_Available: { $gt: 0 } };
            sort = { sodAmount_FT_Available: -1 };
        } else {
            params = { ...params, sodAllowSellADA: STATUS_ALLOW_SELL, sodAmount_ADA_Available: { $gt: 0 } };
            sort = { sodAmount_ADA_Available: -1 };
        }
        //--------
        let swapOffers: SwapOfferEntity[] = await SwapOfferApi.getByParamsApi_(params, { lookUpFields, limit: 20, sort });
        const swapOffersSorted = swapOffers.sort((a: SwapOfferEntity, b: SwapOfferEntity) => {
            if (isBuy) {
                // Sort by sodAmount_FT_Available in descending order
                if (b.sodAmount_FT_Available !== a.sodAmount_FT_Available) {
                    return Number(b.sodAmount_FT_Available - a.sodAmount_FT_Available);
                } else {
                    // If tied, sort by sodAskedCommission_InBPx1e3 in ascending order
                    return Number(a.sodAskedCommission_InBPx1e3 - b.sodAskedCommission_InBPx1e3);
                }
            } else {
                // Sort by sodAmount_ADA_Available in descending order
                if (b.sodAmount_ADA_Available !== a.sodAmount_ADA_Available) {
                    return Number(b.sodAmount_ADA_Available - a.sodAmount_ADA_Available);
                } else {
                    // If tied, sort by sodAskedCommission_InBPx1e3 in ascending order
                    return Number(a.sodAskedCommission_InBPx1e3 - b.sodAskedCommission_InBPx1e3);
                }
            }
        });
        return swapOffersSorted;
    };
    //--------------------------------------
    const {
        isLoadingList: isLoadingSwapOffers,
        isLoadedList: isLoadedSwapOffers,
        list: swapOffers,
        current: swapOffer,
        refreshList: refreshListSwapOffers,
        setCurrent: setSwapOffer_,
    } = useList<SwapOfferEntity>({
        nameList: SwapOfferEntity.className(),
        loadList: loadSwapOfferList,
        dependencies: [isBuy, props.isLoadedDetails],
        checkDependencies: () => {
            //--------------------------------------
            let doLoadList = false;
            if (props.isLoadedDetails === true) {
                doLoadList = true;
            }
            return doLoadList;
        },
        //--------------------------------------
    });
    //--------------------------------------
    async function resetForm() {
        setAmount_FT(0n);
        setAmount_ADA(0n);
        setAmount_AfterCommission_FT(0n);
        setAmount_AfterCommission_ADA(0n);
        setCommission_FT(0n);
        setCommission_ADA(0n);
        setPrice_Adjusted_For_Fee(undefined);
        setSwapOffer(undefined);
    }
    //--------------------------------------
    const onTx = async () => {
        resetForm();
        await props.refreshDetails(fund!._DB_id);
        await props.refreshList();
    };
    const onTryAgainTx = async () => {
        setIsFaildedTx(false);
        setShowProcessingTx(false);
    };
    const onFinishTx = async () => {
        // si la tx paso, ya se reseto el form en onTx, pero por las dudas, si aprienta cuando falla
        if (isFaildedTx === true) {
            resetForm();
        }
        setIsConfirmedTx(false);
        setIsFaildedTx(false);
        setShowProcessingTx(false);
    };
    //--------------------------------------
    const { isTokensStoreReady, isAddingTokens } = useTokensStoreForFunds({
        mode: 'FundDetails',
        name: 'FundDetailsForSwaps',
        isLoadedDetails: props.isLoadedDetails,
        fund: fund,
        followUp: true,
        keepAlive: undefined,
        addTokenFT: true,
        addSubTokensFT: false,
        swAddPrice: true,
        swAddMetadata: true,
        initPriceUpdater: true,
    });
    //--------------------------------------
    async function checkIsValidTx() {
        let isValid =
            props.isLoadedDetails === true &&
            isValidInputToGive === true &&
            isValidInputToGet === true &&
            isMaxAmountLoaded === true &&
            isPriceValid === true &&
            isLoadedSwapOffers === true &&
            swapOffer !== undefined &&
            swapOffer.smartUTxO?.isAvailableForConsuming() === true &&
            isTokensStoreReady === true;
        if (isBuy === true) {
            isValid = isValid && amount_ADA > 0 && amount_FT > 0;
        } else {
            isValid = isValid && amount_FT > 0 && amount_ADA > 0;
        }
        return isValid;
    }
    //--------------------------------------
    const dependenciesValidTx: any[] = [
        isValidInputToGive,
        isValidInputToGet,
        amount_ADA,
        amount_FT,
        swapOffer,
        isBuy,
        isLoadedSwapOffers,
        isPriceValid,
        props.isLoadedDetails,
        isTokensStoreReady,
    ];
    //--------------------------------------
    const {
        appStore,
        walletStore,
        tokensStore,
        session,
        status,
        showUserConfirmation,
        setShowUserConfirmation,
        showProcessingTx,
        setShowProcessingTx,
        isProcessingTx,
        setIsProcessingTx,
        isFaildedTx,
        setIsFaildedTx,
        isConfirmedTx,
        setIsConfirmedTx,
        processingTxMessage,
        setProcessingTxMessage,
        processingTxHash,
        setProcessingTxHash,
        isValidTx,
        setIsValidTx,
        tokensGiveWithMetadata,
        setTokensGiveWithMetadata,
        tokensGetWithMetadata,
        setTokensGetWithMetadata,
        available_TokenGov_in_Wallet,
        available_FT_in_Wallet,
        available_ADA_in_Wallet,
        available_forSpend_ADA_in_Wallet,
        isMaxAmountLoaded,
        handleBtnShowUserConfirmation,
        handleBtnDoTransaction_WithErrorControl,
    } = useTransactions({ ...props, dependenciesValidTx, checkIsValidTx, onTx });
    //--------------------------------------
    // Store the user-selected swap offer for pro mode
    const swapOfferRef = useRef<SwapOfferEntity | undefined>();
    //--------------------------------------
    // Wrapper function to update both the ref and state
    const setSwapOffer = (current?: SwapOfferEntity | undefined) => {
        swapOfferRef.current = current;
        setSwapOffer_(current);
    };
    //--------------------------------------
    // Select the appropriate swap offer based on mode and requirements
    function selectSwapOffer(swapOffers: SwapOfferEntity[]) {
        if (swapOffers.length === 0) {
            setSwapOffer(undefined);
            return;
        }
        if (walletStore.swProInterface === false) {
            // In basic mode, find the minimal offer that covers the user's needs
            // Automatic selection mode: Find the smallest offer that covers the required amount
            let selectedOffer;
            if (isBuy) {
                selectedOffer = [...swapOffers] // Clone array to avoid mutation
                    .reverse() // Reverse to check smallest offers first
                    .find((offer) => offer.sodAmount_FT_Available >= amount_AfterCommission_FT);
            } else {
                selectedOffer = [...swapOffers].reverse().find((offer) => offer.sodAmount_ADA_Available >= amount_AfterCommission_ADA);
            }
            setSwapOffer(selectedOffer || swapOffers[0]); // Pick bigger one if no match
        } else {
            // In pro mode, maintain user selection or default to first offer
            // Manual selection mode: Keep the previous selection or pick the first one
            if (swapOfferRef.current !== undefined) {
                const prevSwapOffer = swapOffers.find((so) => so._DB_id === swapOfferRef.current?._DB_id);
                setSwapOffer(prevSwapOffer || swapOffers[0]);
            } else {
                setSwapOffer(swapOffers[0]);
            }
        }
    }
    //--------------------------------------
    // Update selected swap offer when offer list changes
    useEffect(() => {
        if (isLoadedSwapOffers) {
            selectSwapOffer(swapOffers);
        }
    }, [swapOffers, isLoadedSwapOffers]);
    //--------------------------------------
    useEffect(() => {
        if (swapOffer === undefined) {
            setMaxAmount_ADA_Available(undefined);
            setMaxAmount_FT_Available(undefined);
        } else {
            if (walletStore.swProInterface === false) {
                // find max value on all offers
                const maxAmount_ADA = swapOffers.reduce((max, offer) => (offer.sodAmount_ADA_Available > max ? offer.sodAmount_ADA_Available : max), 0n);
                const maxAmount_FT = swapOffers.reduce((max, offer) => (offer.sodAmount_FT_Available > max ? offer.sodAmount_FT_Available : max), 0n);
                setMaxAmount_ADA_Available(maxAmount_ADA);
                setMaxAmount_FT_Available(maxAmount_FT);
            } else {
                setMaxAmount_ADA_Available(swapOffer.sodAmount_ADA_Available);
                setMaxAmount_FT_Available(swapOffer.sodAmount_FT_Available);
            }
        }
    }, [swapOffer, walletStore.swProInterface]);
    //--------------------------------------
    useEffect(() => {
        if (walletStore.swProInterface === false) {
            // If Pro Mode is off, re-select the best offer when user changes inputs
            // Re-evaluate offer selection when amount changes in basic mode
            selectSwapOffer(swapOffers);
        }
    }, [amount_AfterCommission_FT, amount_AfterCommission_ADA]);
    //--------------------------------------
    useEffect(() => {
        // Periodically refresh the swap offer list
        const refresh = async () => {
            if (isProcessingTx === false && isConfirmedTx === false && showUserConfirmation === false) {
                console.log('[SWAPS] Refreshing swapOffers');
                await refreshListSwapOffers();
            }
        };
        const intervalId = setInterval(refresh, SWAPS_REFRESH_TIME_MS);
        return () => clearInterval(intervalId);
    }, []);
    //--------------------------------------
    useEffect(() => {
        if (fund !== undefined) {
            resetForm();
        }
    }, [fund]);
    //--------------------------------------
    useEffect(() => {
        setSwapOffer(undefined);
    }, [isBuy]);
    //--------------------------------------
    useEffect(() => {
        // cuando cambia isBuy, seteo SwapOffer undefined. Por otro lado la lista de swap offers se carga y eso trigea el use effect de arriba donde se setea un nuevo valor en SwapOffer y eso hace que se ejecute este useEffect
        if (isBuy === true) {
            if (lastChanged.current === 'GET') {
                calculate_Buy_FT_from_FT_toGet(amount_AfterCommission_FT);
            } else {
                calculate_Buy_FT_from_ADA_toGive(amount_ADA);
            }
        } else {
            if (lastChanged.current === 'GET') {
                calculate_Sell_FT_from_ADA_ToGet(amount_AfterCommission_ADA);
            } else {
                calculate_Sell_FT_from_FT_ToGive(amount_FT);
            }
        }
        calculate_Price_Adjusted_For_Fee();
    }, [swapOffer, tokensStore.tokensWithDetails]);
    //--------------------------------------
    useEffect(() => {
        setIsPriceValid(fund !== undefined && tokensStore.isTokenPriceValid(fund.getNET_id_CS(), fund.fdFundFT_TN_Hex));
    }, [tokensStore.tokensWithDetailsAndValidity]);
    //--------------------------------------
    useEffect(() => {
        // para asegurarme de que los precios esten actualizados cuando carga la pagina asi el cotnador de tiempo esta al maximo
        if (isTokensStoreReady === true) {
            tokensStore.refreshPrices({ forceRefresh: true });
        }
    }, [isTokensStoreReady]);
    //--------------------------------------
    useEffect(() => {
        console.log('[SWAPS] setDoUpdatePricesAutomatically', showUserConfirmation === false);
        tokensStore.setDoUpdatePricesAutomatically(showUserConfirmation === false);
    }, [showUserConfirmation]);
    //--------------------------------------
    useEffect(() => {
        if (showUserConfirmation === true && fund !== undefined) {
            if (isBuy === true) {
                const tokensToGet = [createToken_With_Amount(fund.getNET_id_CS(), fund.fdFundFT_TN_Hex, fund.tokenFT?.ticker, amount_AfterCommission_FT)];
                setTokensGetWithMetadata(tokensToGet.filter((token) => token && token.amount > 0));
                const tokensToGive = [createToken_ADA_With_Amount(amount_ADA)];
                setTokensGiveWithMetadata(tokensToGive.filter((token) => token && token.amount > 0));
            } else {
                const tokensToGet = [createToken_ADA_With_Amount(amount_AfterCommission_ADA)];
                setTokensGetWithMetadata(tokensToGet.filter((token) => token && token.amount > 0));
                const tokensToGive = [createToken_With_Amount(fund.getNET_id_CS(), fund.fdFundFT_TN_Hex, fund.tokenFT?.ticker, amount_FT)];
                setTokensGiveWithMetadata(tokensToGive.filter((token) => token && token.amount > 0));
            }
        }
    }, [showUserConfirmation, fund, isBuy, amount_ADA, amount_AfterCommission_FT, amount_FT, amount_AfterCommission_ADA]);
    //--------------------------------------
    function calculate_Buy_FT_from_ADA_toGive(amount_ADA_toGive: bigint) {
        if (swapOffer !== undefined && fund !== undefined) {
            // console.log('calculate_Buy_FT_from_ADA_toGive', amount_ADA_toGive);
            const tokenFTWithPriceAndValidity = tokensStore.getTokenPriceAndMetadata(fund.getNET_id_CS(), fund.fdFundFT_TN_Hex);
            if (tokenFTWithPriceAndValidity !== undefined && tokenFTWithPriceAndValidity.priceADAx1e6 !== undefined) {
                const {
                    amount_FT_toGet: amount_AfterCommission_FT,
                    commission_FT,
                    total_FT: amount_FT,
                } = SwapOfferEntity.calculate_Buy_FT_from_ADA_toGive(amount_ADA_toGive, tokenFTWithPriceAndValidity.priceADAx1e6, swapOffer.sodAskedCommission_InBPx1e3);
                setAmount_FT(amount_FT);
                setCommission_FT(commission_FT);
                setAmount_AfterCommission_FT(amount_AfterCommission_FT);
                console.log(
                    '[SWAPS] ',
                    'calculate_Buy_FT_from_ADA_toGive',
                    'amount_ADA_toGive',
                    amount_ADA_toGive,
                    'amount_AfterCommission_FT:',
                    amount_AfterCommission_FT,
                    'commission_FT:',
                    commission_FT,
                    'amount_FT:',
                    amount_FT
                );
            }
        }
    }
    function calculate_Buy_FT_from_FT_toGet(amount_FT_toGet: bigint) {
        if (swapOffer !== undefined && fund !== undefined) {
            const tokenFTWithPriceAndValidity = tokensStore.getTokenPriceAndMetadata(fund.getNET_id_CS(), fund.fdFundFT_TN_Hex);
            if (tokenFTWithPriceAndValidity !== undefined && tokenFTWithPriceAndValidity.priceADAx1e6 !== undefined) {
                const {
                    amount_ADA_toGive: amount_ADA,
                    commission_FT,
                    total_FT: amount_FT,
                } = SwapOfferEntity.calculate_Buy_FT_from_FT_toGet(amount_FT_toGet, tokenFTWithPriceAndValidity.priceADAx1e6, swapOffer.sodAskedCommission_InBPx1e3);
                setAmount_FT(amount_FT);
                setCommission_FT(commission_FT);
                setAmount_ADA(amount_ADA);
                console.log(
                    '[SWAPS] ',
                    'calculate_Buy_FT_from_FT_toGet',
                    'amount_FT_toGet',
                    amount_FT_toGet,
                    'amount_ADA:',
                    amount_ADA,
                    'commission_FT:',
                    commission_FT,
                    'amount_FT:',
                    amount_FT
                );
            }
        }
    }
    function calculate_Sell_FT_from_FT_ToGive(amount_FT_toGive: bigint) {
        if (swapOffer !== undefined && fund !== undefined) {
            const tokenFTWithPriceAndValidity = tokensStore.getTokenPriceAndMetadata(fund.getNET_id_CS(), fund.fdFundFT_TN_Hex);
            if (tokenFTWithPriceAndValidity !== undefined && tokenFTWithPriceAndValidity.priceADAx1e6 !== undefined) {
                const {
                    total_ADA: amount_ADA,
                    commission_ADA,
                    amount_ADA_toGet: amount_AfterCommission_ADA,
                } = SwapOfferEntity.calculate_Sell_FT_from_FT_ToGive(amount_FT_toGive, tokenFTWithPriceAndValidity.priceADAx1e6, swapOffer.sodAskedCommission_InBPx1e3);
                setAmount_ADA(amount_ADA);
                setCommission_ADA(commission_ADA);
                setAmount_AfterCommission_ADA(amount_AfterCommission_ADA);
                console.log(
                    '[SWAPS] ',
                    'calculate_Sell_FT_from_FT_ToGive',
                    'amount_FT_toGive',
                    amount_FT_toGive,
                    'amount_ADA:',
                    amount_ADA,
                    'commission_ADA:',
                    commission_ADA,
                    'amount_AfterCommission_ADA:',
                    amount_AfterCommission_ADA
                );
            }
        }
    }
    function calculate_Sell_FT_from_ADA_ToGet(amount_ADA_toGet: bigint) {
        if (swapOffer !== undefined && fund !== undefined) {
            const tokenFTWithPriceAndValidity = tokensStore.getTokenPriceAndMetadata(fund.getNET_id_CS(), fund.fdFundFT_TN_Hex);
            if (tokenFTWithPriceAndValidity !== undefined && tokenFTWithPriceAndValidity.priceADAx1e6 !== undefined) {
                const {
                    amount_FT_toGive: amount_FT,
                    commission_ADA,
                    total_ADA: amount_AfterCommission_ADA,
                } = SwapOfferEntity.calculate_Sell_FT_from_ADA_ToGet(amount_ADA_toGet, tokenFTWithPriceAndValidity.priceADAx1e6, swapOffer.sodAskedCommission_InBPx1e3);
                setAmount_ADA(amount_ADA);
                setCommission_ADA(commission_ADA);
                setAmount_FT(amount_FT);
                console.log(
                    '[SWAPS] ',
                    'calculate_Sell_FT_from_ADA_ToGet',
                    'amount_ADA_toGet',
                    amount_ADA_toGet,
                    'amount_FT:',
                    amount_FT,
                    'commission_ADA:',
                    commission_ADA,
                    'amount_AfterCommission_ADA:',
                    amount_AfterCommission_ADA
                );
            }
        }
    }
    //--------------------------------------
    function calculate_Price_Adjusted_For_Fee() {
        if (swapOffer !== undefined) {
            const priceAdjusted = getPrice_Adjusted_For_Fee(swapOffer);
            setPrice_Adjusted_For_Fee(priceAdjusted);
        }
    }
    function getPrice_Adjusted_For_Fee(swapOffer: SwapOfferEntity) {
        if (fund !== undefined) {
            const tokenFTWithPriceAndValidity = tokensStore.getTokenPriceAndMetadata(fund.getNET_id_CS(), fund.fdFundFT_TN_Hex);
            if (tokenFTWithPriceAndValidity !== undefined && tokenFTWithPriceAndValidity.priceADAx1e6 !== undefined) {
                if (isBuy === true) {
                    // 🔹 Precio ajustado = Precio real * (BP_DENOMINATOR / (BP_DENOMINATOR - Fee))
                    const BP_DENOMINATOR = 10000000n;
                    const denominator = BP_DENOMINATOR - swapOffer.sodAskedCommission_InBPx1e3;
                    const multiplied = tokenFTWithPriceAndValidity.priceADAx1e6 * BP_DENOMINATOR;
                    const remainder = multiplied % denominator;
                    const result = multiplied / denominator;
                    const resultRouded = remainder > 0n ? result + 1n : result;
                    return resultRouded;
                } else {
                    // 🔹 Precio ajustado = Precio real * ((BP_DENOMINATOR - Fee) / BP_DENOMINATOR)
                    const BP_DENOMINATOR = 10000000n;
                    const numerator = BP_DENOMINATOR - swapOffer.sodAskedCommission_InBPx1e3; // 1 - Fee%
                    const multiplied = tokenFTWithPriceAndValidity.priceADAx1e6 * numerator;
                    const remainder = multiplied % BP_DENOMINATOR;
                    const result = multiplied / BP_DENOMINATOR;
                    return remainder > 0n ? result + 1n : result;
                }
            }
        }
    }
    //--------------------------------------
    function show_SwapOffer(swapOffer: SwapOfferEntity) {
        if (fund === undefined) {
            return '';
        }
        // "Up to 500 FT at 5%",
        let upToStr;
        if (isBuy === true) {
            upToStr = `${showToken_Amount_Rounded(tokensStore, swapOffer.sodAmount_FT_Available, fund.getNET_id_CS(), fund.fdFundFT_TN_Hex)}`;
        } else {
            upToStr = `
            ${showADA_Amount_Rounded(tokensStore, swapOffer.sodAmount_ADA_Available)}`;
        }
        const priceAdjusted = getPrice_Adjusted_For_Fee(swapOffer);
        // const porcentageStr = formatPercentage(swapOffer.sodAskedCommission_InBPx1e3, 2, false, 5);
        if (swapOffer.smartUTxO?.isAvailableForConsuming() === false) {
            return `Not available`;
        } else {
            // return `Up to ${upToStr} at ${porcentageStr}`;
            return `Up to ${upToStr} at ${
                tokensStore.isAddingTokens === false ? showADA_Amount_NotRounded(tokensStore, priceAdjusted) : showADA_Amount_NotRounded(tokensStore, undefined)
            }`;
        }
    }
    //--------------------------------------
    const handleClickToggleBuySell = () => {
        if (isBuy === true) {
            setIsBuy(false);
            lastChanged.current = 'GIVE';
            setAmount_FT(amount_AfterCommission_FT);
        } else {
            setIsBuy(true);
            lastChanged.current = 'GET';
            setAmount_AfterCommission_FT(amount_FT);
        }
    };
    //--------------------------------------
    function handleChange_toGive(amount_toGive: bigint, isBuy_?: boolean) {
        isBuy_ = isBuy_ ?? isBuy;
        console.log('[SWAPS] handleChange_toGive', amount_toGive);
        lastChanged.current = 'GIVE';
        if (isBuy_ === true) {
            setAmount_ADA(amount_toGive);
            calculate_Buy_FT_from_ADA_toGive(amount_toGive);
        } else {
            setAmount_FT(amount_toGive);
            calculate_Sell_FT_from_FT_ToGive(amount_toGive);
        }
    }
    function handleChange_toGet(amount_toGet: bigint, isBuy_?: boolean) {
        isBuy_ = isBuy_ ?? isBuy;
        console.log('[SWAPS] handleChange_toGet', amount_toGet);
        lastChanged.current = 'GET';
        if (isBuy_ === true) {
            setAmount_AfterCommission_FT(amount_toGet);
            calculate_Buy_FT_from_FT_toGet(amount_toGet);
        } else {
            setAmount_AfterCommission_ADA(amount_toGet);
            calculate_Sell_FT_from_ADA_ToGet(amount_toGet);
        }
    }
    //--------------------------------------
    const handleBtnSwapADAxFTTx = async (amount_FT_toGet: bigint) => {
        //--------------------------------------
        const fetchParams = async () => {
            //--------------------------------------
            if (protocol === undefined || fund === undefined || swapOffer === undefined) {
                throw 'protocol === undefined || fund === undefined || swapOffer === undefined';
            }
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const tokenFTWithPriceAndValidity = tokensStore.getTokenPriceAndMetadata(fund.getNET_id_CS(), fund.fdFundFT_TN_Hex);
            if (tokenFTWithPriceAndValidity === undefined || tokenFTWithPriceAndValidity.priceADAx1e6 === undefined) {
                throw 'tokenFTWithPriceAndValidity is undefined or tokenFTWithPriceAndValidity.priceADAx1e6 is undefined';
            }
            //--------------------------------------
            const token_FT: Token_With_Price_And_Date_And_Signature = tokenFTWithPriceAndValidity;
            //--------------------------------------
            const txParams: SwapOfferSwapADAxFTTxParams = {
                protocol_id: protocol._DB_id,
                fund_id: fund._DB_id,
                swapOffer_id: swapOffer._DB_id,
                amount_AfterCommission_FT: amount_FT_toGet,
                token_FT,
            };
            return {
                lucid,
                emulatorDB,
                walletTxParams,
                txParams,
            };
        };
        //--------------------------------------
        const txApiCall = SwapOfferTxApiCalls.callGenericTxApi.bind(SwapOfferTxApiCalls);
        const handleBtnTx = SwapOfferBtnHandlers.handleBtnDoTransaction_V2_NoErrorControl.bind(SwapOfferBtnHandlers);
        //--------------------------------------
        await handleBtnDoTransaction_WithErrorControl(SwapOfferEntity, `Buy FT Tx`, 'Buying FT...', 'swap-ada-x-ft', fetchParams, txApiCall, handleBtnTx);
        //--------------------------------------
    };
    const handleBtnSwapFTxADATx = async (amount_FT_toGive: bigint) => {
        //--------------------------------------
        const fetchParams = async () => {
            //--------------------------------------
            if (protocol === undefined || fund === undefined || swapOffer === undefined) {
                throw 'protocol === undefined || fund === undefined || swapOffer === undefined';
            }
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const tokenFTWithPriceAndValidity = tokensStore.getTokenPriceAndMetadata(fund.getNET_id_CS(), fund.fdFundFT_TN_Hex);
            if (tokenFTWithPriceAndValidity === undefined || tokenFTWithPriceAndValidity.priceADAx1e6 === undefined) {
                throw 'tokenFTWithPriceAndValidity is undefined or tokenFTWithPriceAndValidity.priceADAx1e6 is undefined';
            }
            //--------------------------------------
            const token_FT: Token_With_Price_And_Date_And_Signature = tokenFTWithPriceAndValidity;
            //--------------------------------------
            const txParams: SwapOfferSwapFTxADATxParams = {
                protocol_id: protocol._DB_id,
                fund_id: fund._DB_id,
                swapOffer_id: swapOffer._DB_id,
                amount_FT: amount_FT_toGive,
                token_FT,
            };
            return {
                lucid,
                emulatorDB,
                walletTxParams,
                txParams,
            };
        };
        //--------------------------------------
        const txApiCall = SwapOfferTxApiCalls.callGenericTxApi.bind(SwapOfferTxApiCalls);
        const handleBtnTx = SwapOfferBtnHandlers.handleBtnDoTransaction_V2_NoErrorControl.bind(SwapOfferBtnHandlers);
        //--------------------------------------
        await handleBtnDoTransaction_WithErrorControl(SwapOfferEntity, 'Sell FT Tx', 'Selling FT...', 'swap-ft-x-ada', fetchParams, txApiCall, handleBtnTx);
        //--------------------------------------
    };
    const handleBtnSwapTx = async () => {
        if (isBuy === true) {
            await handleBtnSwapADAxFTTx(amount_AfterCommission_FT);
        } else {
            await handleBtnSwapFTxADATx(amount_FT);
        }
    };
    //--------------------------------------

    return (
        <>
            {(props.isLoadedDetails === true && fund !== undefined) || (props.isLoadingDetails === true && fund !== undefined && fund._DB_id === id) ? (
                <div className={styles.swapContainer}>
                    <div className={styles.header}>
                        <div className={styles.fundSelector}>
                            <h2 className={styles.fundTitle}>{isBuy ? `BUY ${hexToStr(fund.fdFundFT_TN_Hex)}` : `SELL ${hexToStr(fund.fdFundFT_TN_Hex)}`}</h2>
                            {props.isLoadingDetails === true && <LoaderButton />}
                        </div>
                        <div className={styles.toggleGroup}>
                            <TogleViewListOrDetailsController {...props} />
                            <ToggleProController />
                        </div>
                    </div>
                    <SwapForm
                        fund={fund}
                        protocol={protocol}
                        isBuy={isBuy}
                        isTokensStoreReady={isTokensStoreReady}
                        isAddingTokens={isAddingTokens}
                        isLoadingSwapOffers={isLoadingSwapOffers}
                        swapOffers={swapOffers}
                        swapOffer={swapOffer}
                        setSwapOffer={setSwapOffer}
                        isValidInputToGive={isValidInputToGive}
                        setIsValidInputToGive={setIsValidInputToGive}
                        isValidInputToGet={isValidInputToGet}
                        setIsValidInputToGet={setIsValidInputToGet}
                        amount_ADA={amount_ADA}
                        amount_FT={amount_FT}
                        amount_AfterCommission_FT={amount_AfterCommission_FT}
                        commission_ADA={commission_ADA}
                        commission_FT={commission_FT}
                        amount_AfterCommission_ADA={amount_AfterCommission_ADA}
                        maxAmount_FT_Available={maxAmount_FT_Available}
                        maxAmount_ADA_Available={maxAmount_ADA_Available}
                        price_Adjusted_For_Fee={price_Adjusted_For_Fee}
                        isValidTx={isValidTx}
                        isProcessingTx={isProcessingTx}
                        setShowProcessingTx={setShowProcessingTx}
                        setShowUserConfirmation={setShowUserConfirmation}
                        available_ADA_in_Wallet={available_ADA_in_Wallet}
                        available_forSpend_ADA_in_Wallet={available_forSpend_ADA_in_Wallet}
                        handleChange_toGet={handleChange_toGet}
                        handleChange_toGive={handleChange_toGive}
                        handleClickToggleBuySell={handleClickToggleBuySell}
                        show_SwapOffer={show_SwapOffer}
                    />
                </div>
            ) : (
                <div className={`${styles.swapContainer} ${styles.loader} ${walletStore.swProInterface === false ? styles.basic : styles.pro}`}>
                    <div className={styles.header}>
                        <div className={styles.fundSelector}>
                            <h2 className={styles.fundTitle}></h2>
                        </div>
                        <div className={styles.toggleGroup}>
                            <TogleViewListOrDetailsController {...props} />
                            <ToggleProController />
                        </div>
                    </div>
                    <div className={styles.formController}>
                        <div className={styles.inputController}>
                            <div className={styles.inputGroup}>
                                <div className={styles.infoToken}>
                                    <div className={styles.token}></div>
                                    <div className={styles.input}></div>
                                </div>
                            </div>
                            <div className={styles.inputGroup}>
                                <div className={styles.infoToken}>
                                    <div className={styles.token}></div>
                                    <div className={styles.input}></div>
                                </div>
                            </div>
                        </div>
                        {walletStore.swProInterface === true && (
                            <div className={styles.swapProSection}>
                                <div className={styles.offerSection}>
                                    <div className={styles.rowLoader}></div>
                                </div>
                                <div className={styles.conversionSection}>
                                    <div className={styles.rowLoader}></div>
                                    <div className={styles.rowLoader}></div>
                                    <div className={styles.rowLoader}></div>
                                </div>
                            </div>
                        )}
                        <div className={styles.confirmationSection}>
                            <LoaderButton classNameStyle="black" />
                            LOADING...
                        </div>
                    </div>
                </div>
            )}
            {showUserConfirmation && walletStore.isConnected === true && swapOffer !== undefined && fund !== undefined && (
                <>
                    <TxUserConfirmationModal
                        title={isBuy ? `BUY ${hexToStr(fund.fdFundFT_TN_Hex)}` : `SELL ${hexToStr(fund.fdFundFT_TN_Hex)}`}
                        protocol={protocol}
                        fund={fund}
                        isValidTx={isValidTx}
                        btnTxInvalidName={'Price is outdated please try again'}
                        tokensGive={tokensGiveWithMetadata}
                        tokensGet={tokensGetWithMetadata}
                        showExtraInfo={walletStore.swProInterface === true}
                        extraInfo={
                            walletStore.swProInterface === true && (
                                <Conversion
                                    isBuy={isBuy}
                                    fund={fund}
                                    swapOffer={swapOffer}
                                    showRefresh={false}
                                    showValid={isProcessingTx === false}
                                    commission={isBuy ? commission_FT : commission_ADA}
                                    classNameStyle="darker"
                                    price_Adjusted_For_Fee={price_Adjusted_For_Fee}
                                />
                            )
                        }
                        onConfirm={async () => handleBtnSwapTx()}
                        onCancel={async () => setShowUserConfirmation(false)}
                    />
                </>
            )}
            <TxProcessingModal
                showProcessingTx={showProcessingTx}
                setShowProcessingTx={setShowProcessingTx}
                isProcessingTx={isProcessingTx}
                processingTxMessage={processingTxMessage}
                processingTxHash={processingTxHash}
                isFaildedTx={isFaildedTx}
                isConfirmedTx={isConfirmedTx}
                onTryAgainTx={onTryAgainTx}
                onFinishTx={onFinishTx}
                showNewTransactionButton={false}
                showViewTransactionsButton={false}
                showTryAgainTransactionButton={true}
                othersButtonsWhenConfirmed={
                    <>
                        <Button
                            mode="outline"
                            width={'100%'}
                            onClick={async () => {
                                setShowProcessingTx(false);
                                await router.push({
                                    pathname: ROUTES.PORTFOLIO,
                                });
                            }}
                        >
                            View Portfolio
                        </Button>
                    </>
                }
            />
        </>
    );
}
